import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React from 'react';

/** 0.734 -> "73%"; a missing rate is a dash, never 0%. */
export const pct = (value: number | null | undefined): string =>
    value === null || value === undefined ? '–' : `${Math.round(value * 100)}%`;

const BAR_HEIGHT = 8;
const GAP = 2;

/**
 * A thin horizontal bar for a rate, value at the tip. One hue, a recessive track, a 4px
 * rounded data end; a missing rate draws the track alone.
 */
export const RateBar: React.FC<{ value: number | null; label?: string; muted?: boolean }> = ({
    value,
    label,
    muted = false,
}) => {
    const theme = useTheme();
    const fill = muted ? theme.palette.text.disabled : theme.palette.primary.main;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 130 }}>
            <Box
                role="img"
                aria-label={label ?? `${pct(value)}`}
                sx={{ flex: 1, height: BAR_HEIGHT, borderRadius: '4px', bgcolor: alpha(theme.palette.text.primary, 0.08) }}
            >
                {value !== null && (
                    <Box sx={{ width: `${Math.max(value, 0.02) * 100}%`, height: '100%', borderRadius: '0 4px 4px 0', bgcolor: fill }} />
                )}
            </Box>
            <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {pct(value)}
            </Typography>
        </Box>
    );
};

export interface ShareItem {
    key: string;
    label: string;
    count: number;
    share: number;
}

/** Ranked horizontal bars; each is a button that selects the cause it stands for. */
export const ShareBars: React.FC<{
    items: ShareItem[];
    selected: string | null;
    onSelect: (key: string | null) => void;
}> = ({ items, selected, onSelect }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'grid', gap: 0.5 }} data-testid="share-bars">
            {items.map((item) => {
                const active = item.key === selected;
                return (
                    <ButtonBase
                        key={item.key}
                        onClick={() => onSelect(active ? null : item.key)}
                        aria-pressed={active}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(120px, 220px) 1fr auto',
                            gap: 1.5,
                            alignItems: 'center',
                            textAlign: 'left',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                        }}
                    >
                        <Typography variant="body2">{item.label}</Typography>
                        <Box sx={{ height: BAR_HEIGHT, borderRadius: '4px', bgcolor: alpha(theme.palette.text.primary, 0.08) }}>
                            <Box
                                sx={{
                                    width: `${Math.max(item.share, 0.02) * 100}%`,
                                    height: '100%',
                                    borderRadius: '0 4px 4px 0',
                                    bgcolor: theme.palette.primary.main,
                                }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {item.count} · {pct(item.share)}
                        </Typography>
                    </ButtonBase>
                );
            })}
        </Box>
    );
};

export interface WeekColumn {
    week: string;
    rated: number;
    up: number;
    down: number;
    satisfaction: number | null;
}

const COLUMN_AREA = 120;

/** Satisfaction per week as columns (at most 24px wide), the number rated under each. */
export const WeeklyColumns: React.FC<{ weeks: WeekColumn[] }> = ({ weeks }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', overflowX: 'auto', pb: 1 }} data-testid="weekly-columns">
            {weeks.map((week) => (
                <Tooltip
                    key={week.week}
                    arrow
                    title={`Week of ${week.week}: ${pct(week.satisfaction)} satisfied (${week.up} up, ${week.down} down)`}
                >
                    <Box sx={{ width: 40, display: 'grid', justifyItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {pct(week.satisfaction)}
                        </Typography>
                        <Box sx={{ height: COLUMN_AREA, width: 24, display: 'flex', alignItems: 'flex-end', bgcolor: alpha(theme.palette.text.primary, 0.05), borderRadius: '4px 4px 0 0' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: `${Math.max((week.satisfaction ?? 0) * 100, 2)}%`,
                                    borderRadius: '4px 4px 0 0',
                                    bgcolor: theme.palette.primary.main,
                                }}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {week.week.slice(5)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            n={week.rated}
                        </Typography>
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
};

/** better / same / worse as one stacked bar (2px gaps between segments) with a written legend. */
export const VerdictBar: React.FC<{ verdicts: { better: number; same: number; worse: number; unjudged: number } }> = ({
    verdicts,
}) => {
    const theme = useTheme();
    const total = verdicts.better + verdicts.same + verdicts.worse + verdicts.unjudged;
    const parts = [
        { key: 'better', label: 'Better', count: verdicts.better, color: theme.palette.success.main },
        { key: 'same', label: 'Same', count: verdicts.same, color: theme.palette.text.disabled },
        { key: 'worse', label: 'Worse', count: verdicts.worse, color: theme.palette.error.main },
        { key: 'unjudged', label: 'Unjudged', count: verdicts.unjudged, color: alpha(theme.palette.text.primary, 0.12) },
    ];
    return (
        <Box sx={{ minWidth: 200 }}>
            <Box sx={{ display: 'flex', gap: `${GAP}px`, height: BAR_HEIGHT }} role="img" aria-label={parts.map((p) => `${p.count} ${p.label.toLowerCase()}`).join(', ')}>
                {total === 0 && <Box sx={{ flex: 1, borderRadius: '4px', bgcolor: alpha(theme.palette.text.primary, 0.08) }} />}
                {parts
                    .filter((part) => part.count > 0)
                    .map((part) => (
                        <Tooltip key={part.key} title={`${part.label}: ${part.count}`}>
                            <Box sx={{ flex: part.count, borderRadius: '4px', bgcolor: part.color }} />
                        </Tooltip>
                    ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
                {parts.map((part) => (
                    <Box key={part.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: part.color }} />
                        <Typography variant="caption" color="text.secondary">
                            {part.label} {part.count}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

/** One headline number: label above, value, and a note for the count behind it. */
export const StatTile: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
    <Box sx={{ minWidth: 140, flex: '1 1 140px' }}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {value}
        </Typography>
        {note && (
            <Typography variant="caption" color="text.secondary">
                {note}
            </Typography>
        )}
    </Box>
);
